import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronRight,
  Link2,
  LogOut,
  MessagesSquare,
  Newspaper,
  Palette,
  Sparkles,
  SlidersHorizontal,
  Target,
  UserRound,
  Wallet,
  Wand2,
} from 'lucide-react';
import { Suspense, type ComponentType, type SVGProps } from 'react';

import { env } from '@/env';
import { useAppSelector } from '@/store/hooks';
import { selectUnreadInboxCount } from '@/store/engagement.slice';
import { useLogtoAuth } from '@/hooks/useLogtoAuth';
import { usePuoCreareAvatar } from '@/hooks/usePuoCreareAvatar';
import { cn } from '@/lib/cn';
import { useV3 } from '@/lib/v3';
import { VoceSondaCattura } from '@/lib/sondaCattura';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { RigaGioco } from './RigaGioco';

/**
 * L'elenco delle impostazioni, più il logout.
 *
 * Estratto dalla schermata `/settings` perché ora vive in DUE posti: quella
 * schermata e la tasca Impostazioni dello Zaino. Copiarlo avrebbe significato
 * due elenchi che divergono al primo che ne aggiorna uno solo — e il modo in cui
 * si scopre è che una voce esiste in un posto e non nell'altro.
 *
 * Non porta intestazione: il titolo lo mette chi lo ospita, perché nei due posti
 * si chiama in modo diverso ed è a un'altezza diversa della gerarchia.
 *
 * ⚠️ `conStudio` e' SPENTO per difetto, e lo accende SOLO la schermata `/settings` (`SettingsHome`): la voce
 * «Studio avatar» NON va nello Zaino del Profilo (vincolo di tma-orcb, 2026-10-05; I-416), che monta questo
 * stesso elenco. Un default acceso avrebbe messo la voce in entrambi i posti al primo `<SettingsList />` nudo.
 */
export function SettingsList({ className, conStudio = false, conSonda = false }: { className?: string; conStudio?: boolean; conSonda?: boolean }) {
  const { t } = useTranslation('general');
  const { t: tMissions } = useTranslation('missions');
  const { signOut } = useLogtoAuth();
  const unreadInbox = useAppSelector(selectUnreadInboxCount);
  const puoCreare = usePuoCreareAvatar();
  // Un solo punto per i due ospiti (`/settings` e lo Zaino): nella v3 il selettore del gioco non ha «Mostra tutti» (v. `RigaGioco`). In produzione `useV3()` e' `() => false`.
  const v3 = useV3();

  return (
    <div className={cn('flex flex-col gap-6', className)}>
      {/* Il cambio gioco in cima all'elenco, staccato dalle voci: e' una preferenza come le altre, ma e'
          quella che si tocca piu' spesso. Stava SOLO nello Zaino, fuori da questo elenco, e la scheda
          ⚙️ era nata senza (I-334): v. `RigaGioco`. */}
      <RigaGioco senzaMostraTutti={v3} />
      <section className="flex flex-col gap-2">
        {/* Il nickname sta in TESTA, e non e' una preferenza estetica: fino al 2026-09-15
            questo elenco non aveva **nessuna** voce da cui cambiarlo, e l'unico posto in
            cui si poteva scegliere era uno step del wizard di onboarding, che parte una
            volta sola per utente e non e' riapribile. La domanda che l'ha fatta nascere e'
            testuale dell'operatore: «dove imposto il nickname nella TMA? non vedo alcun
            setting in setting o altrove» — ⇒ la risposta deve essere la prima cosa che si
            legge aprendo le impostazioni, non l'ultima. */}
        <SettingsRow
          to="/settings/nickname"
          icon={UserRound}
          title={t('nickname_row_title', { defaultValue: 'Nickname' })}
          subtitle={t('nickname_row_hint', { defaultValue: 'The name other players see' })}
        />
        {/* Il varco STABILE verso /avatars: il banner ne porta uno solo, e solo a chi ha
            gia' un avatar (o il segnaposto, finche' non ne ha). Stessa condizione del
            segnaposto e delle rotte (`router.tsx`): flag spento ⇒ nessuna voce, quindi
            nessun link verso una rotta che porterebbe a `/`. */}
        {env.featureAvatarsEnabled && (
          <SettingsRow
            to="/avatars"
            icon={Sparkles}
            title={t('avatars.row_title', { defaultValue: 'Avatars' })}
            // I-416: con il flag Studio acceso, chi non ha il ruolo `administrator` non crea (Qavatar#10): per lui la riga
            // e' solo la scelta. Flag spento (produzione): «Crea e scegli» per tutti, come oggi.
            subtitle={
              puoCreare
                ? t('avatars.row_hint', { defaultValue: 'Create and pick your avatar' })
                : t('avatars.row_hint_pick', { defaultValue: 'Pick your avatar' })
            }
          />
        )}
        {/* «Studio avatar» (I-416, L-2762): la porta della CREAZIONE, solo per i ruoli creatore, dietro un flag
            opt-in acceso solo su staging. Lo Studio rimanda a `/avatars/new` e all'editor, quindi richiede anche il
            flag degli avatar. Solo nella schermata `/settings`: v. `conStudio` sopra, non nello Zaino. */}
        {conStudio && env.featureAvatarsEnabled && env.featureAvatarStudioEnabled && puoCreare && (
          <SettingsRow
            to="/settings/studio-avatar"
            icon={Wand2}
            title={t('studio_avatar.row_title', { defaultValue: 'Avatar studio' })}
            subtitle={t('studio_avatar.row_hint', { defaultValue: 'Create avatars' })}
          />
        )}
        <SettingsRow
          to="/settings/accounts"
          icon={Link2}
          title={t('social.accounts_title', { defaultValue: 'Accounts' })}
          subtitle={t('social.accounts_row_hint', { defaultValue: 'Games & personal accounts' })}
        />
        <SettingsRow
          to="/settings/communication"
          icon={MessagesSquare}
          title={t('communication', { defaultValue: 'Communication' })}
          subtitle={t('communication_hint', { defaultValue: 'Inbox & chat' })}
          badge={unreadInbox}
        />
        <SettingsRow
          to="/settings/appearance"
          icon={Palette}
          title={t('appearance', { defaultValue: 'Appearance' })}
          subtitle={t('appearanceRowHint', { defaultValue: 'Theme & colours' })}
        />
        <SettingsRow
          to="/news/preferences"
          icon={Newspaper}
          title={t('content', { defaultValue: 'Content' })}
          subtitle={t('contentRowHint', { defaultValue: 'News topics you follow' })}
        />
        <SettingsRow
          to="/settings/preferences"
          icon={SlidersHorizontal}
          title={t('preferences', { defaultValue: 'Preferences' })}
          subtitle={t('preferencesRowHint', { defaultValue: 'Language, haptics, tutorial' })}
        />
        {env.featureQbankEnabled && (
          <SettingsRow
            to="/settings/wallet"
            icon={Wallet}
            title={t('qbank.wallet_levels', { defaultValue: 'Wallet & Levels' })}
            subtitle={t('qbank.wallet_hint', { defaultValue: 'Coins & levels' })}
          />
        )}
        {/* Missioni (Quest) — sotto-sezione del blocco IMPOSTAZIONI, ordine di Luca 2026-08-30.
            Condizionata al flag come gia' fa la riga Wallet qui sopra: quando il flag e' spento la
            riga non c'e', quindi la rotta non e' registrata E non e' raggiungibile da nessuna
            superficie. Le stringhe stanno nel namespace `missions` e non in `general` perche' e'
            li' che vive tutta la copy della sezione — una chiave sola sparsa altrove e' il modo in
            cui i namespace si sfilacciano.

            🔴 RIMESSA QUI il 2026-09-06, e non era qui per un buco del restyle: questo elenco
            NASCE dal Profilo, dove la riga stava, e l'estrazione in `SettingsList` e' partita da
            un ramo del 28/07 che la riga non l'aveva ancora. Senza, `/settings/missions` resta
            registrata in `router.tsx` e **irraggiungibile toccando** — mentre la tasca «Missioni»
            dello Zaino, che invece si tocca, e' un mockup senza `questApi`
            (`components/profile/Missions.tsx:62`). Una vera irraggiungibile e una raggiungibile
            finta: la riga rimette in piedi la prima e non tocca la seconda. */}
        {env.featureMissionsEnabled && (
          <SettingsRow
            to="/settings/missions"
            icon={Target}
            title={tMissions('title')}
            subtitle={tMissions('row_hint')}
          />
        )}
        {/* La sonda di cattura (tma-orcr-95, solo staging): come «Studio avatar», SPENTA per difetto e accesa SOLO da `SettingsHome` (`conSonda`), quindi mai
            nello Zaino. `VoceSondaCattura` e' `null` in produzione (flag letterale, `lib/sondaCattura.ts`); il ruolo `beta` lo controlla la riga stessa. Il ripiego
            dell'ErrorBoundary e' un frammento VUOTO: con `null` un chunk che non si carica mostrerebbe la schermata d'errore intera (v. `GuscioProtetto`). */}
        {conSonda && VoceSondaCattura && (
          <ErrorBoundary fallback={<></>}>
            <Suspense fallback={null}>
              <VoceSondaCattura />
            </Suspense>
          </ErrorBoundary>
        )}
      </section>

      {/* Il logout sta staccato dal resto e in rosso: è l'unica voce di questo
          elenco che non si può annullare tornando indietro. */}
      <button
        type="button"
        onClick={() => void signOut()}
        className="card-edge flex items-center justify-center gap-2 rounded-xl bg-bg-secondary p-3 text-sm font-semibold text-danger transition-opacity active:opacity-80"
      >
        <LogOut className="h-4 w-4" />
        {t('sign_out', { defaultValue: 'Sign out' })}
      </button>
    </div>
  );
}

export function SettingsRow({
  to,
  icon: Icon,
  title,
  subtitle,
  badge,
}: {
  to: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  title: string;
  subtitle?: string;
  badge?: number;
}) {
  return (
    <Link
      to={to}
      className="card-edge flex items-center gap-3 rounded-xl bg-bg-secondary p-3 transition-opacity active:opacity-80"
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-bg-tertiary text-brand">
        <Icon className="h-4 w-4" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-semibold text-fg">{title}</span>
        {subtitle && <span className="truncate text-xs text-fg-muted">{subtitle}</span>}
      </span>
      {badge != null && badge > 0 && (
        <span
          className={cn(
            'inline-flex h-5 min-w-[1.25rem] shrink-0 items-center justify-center rounded-full px-1.5',
            'bg-brand text-[11px] font-bold text-brand-fg',
          )}
        >
          {badge > 99 ? '99+' : badge}
        </span>
      )}
      <ChevronRight aria-hidden className="h-5 w-5 shrink-0 text-fg-muted" />
    </Link>
  );
}
